import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import { PrivaSelectOption } from '@priva/components/select';

import { updatingMultiUserRecentProductBoxNumber } from 'app/state/multi-user-preferences/multi-user-preferences.actions';
import { selectMultiUserRecentProductBoxNumberIfLoaded } from 'app/state/multi-user-preferences/multi-user-preferences.selectors';
import {
    selectProductBoxByNumber,
    selectProductBoxList,
    selectProductBoxListBuNumbers,
} from 'app/state/product-boxes/product-box.selectors';

import { Activity } from '../../features/activity-selector/models';
import { areProductBoxRegistrationArraysEqual } from '../../utils';
import { ProductBox, ProductBoxRegistration } from './../product-box/models';
import { ProductBoxListFormArrayValue } from './product-box-amount-form-value';
import { hasDefaultProductBox } from './utils/product-box-amount.utils';

export interface ProductBoxAmountState {
    activity: Activity | undefined;
    productBoxRegistrations: ProductBoxRegistration[] | undefined;
}

const initialState: ProductBoxAmountState = {
    activity: undefined,
    productBoxRegistrations: undefined,
};

@Injectable()
export class ProductBoxAmountStore extends ComponentStore<ProductBoxAmountState> {
    productBoxNumberFromGlobalStore$: Observable<number> = this.store.pipe(
        selectMultiUserRecentProductBoxNumberIfLoaded,
    );

    hasDefaultProductBox$: Observable<boolean> = this.state$.pipe(
        map(({ activity }) => hasDefaultProductBox(activity)),
    );

    hasDefaultProductBoxDefined$: Observable<boolean> = this.productBoxNumberFromGlobalStore$.pipe(
        map((recentProductNumber) => recentProductNumber !== -1),
    );

    productBoxes$ = combineLatest([this.state$, this.productBoxNumberFromGlobalStore$]).pipe(
        switchMap(([{ activity, productBoxRegistrations }, recentProductBoxNumber]) => {
            // orderedProductBoxNumbers (multiple product boxes) is introduced in FS Performance > v4.6
            if (activity?.orderedProductBoxNumbers && activity?.orderedProductBoxNumbers.length > 0) {
                return this.store.pipe(
                    select(selectProductBoxListBuNumbers(activity.orderedProductBoxNumbers)),
                    map((productBoxList) =>
                        productBoxList.filter((productBox): productBox is ProductBox => !!productBox),
                    ),
                    map((productBoxList) => ({
                        activity,
                        productBoxes: productBoxList,
                        productBoxRegistrations,
                        recentProductBoxNumber,
                        hasDefaultProductBoxNumber: true,
                    })),
                );
            }
            // Backwards compatibility is required for defaultProductBoxNumbers is used in FS Performance < v4.6
            return activity?.defaultProductBoxNumber
                ? this.store.pipe(
                      select(selectProductBoxByNumber(activity.defaultProductBoxNumber)),
                      map(
                          (productBox): ProductBox =>
                              productBox ?? {
                                  name: 'Box ' + activity.defaultProductBoxNumber,
                                  number: activity.defaultProductBoxNumber ?? 0,
                              },
                      ),
                      map((productBox) => ({
                          activity,
                          productBoxes: [productBox],
                          productBoxRegistrations,
                          recentProductBoxNumber,
                          hasDefaultProductBoxNumber: true,
                      })),
                  )
                : this.store.select(selectProductBoxList).pipe(
                      map((productBoxes) => ({
                          activity,
                          productBoxes: productBoxes,
                          productBoxRegistrations,
                          recentProductBoxNumber,
                          hasDefaultProductBoxNumber: false,
                      })),
                  );
        }),
        map(
            ({
                activity,
                productBoxes,
                productBoxRegistrations,
                recentProductBoxNumber,
                hasDefaultProductBoxNumber,
            }) => {
                // Get list of product boxes either from orderedProductBoxNumbers / defaultProductBox or list from activity
                const privaSelectOption = productBoxes.map(
                    // Populate values for Priva dropdown and check the item when the productBox number matches the current state of productBoxRegistration - number
                    (productBox): PrivaSelectOption => ({
                        label: productBox.name || 'Box ' + productBox.number,
                        value: productBox?.number?.toString() ?? '',
                        selected: productBox.number === recentProductBoxNumber,
                    }),
                );

                // If recentProductBoxNumber is not set, set the 'Select product box' as the first item in the list of product boxes and disabled
                // Option 2
                privaSelectOption.unshift({
                    label: 'Select product box',
                    value: '-1',
                    selected: recentProductBoxNumber === -1,
                    disabled: true,
                });
                // List of text input
                let productBoxList: ProductBoxListFormArrayValue[] = [];
                if (hasDefaultProductBoxNumber) {
                    productBoxList = productBoxes.map((productBox) => {
                        const productBoxRegistration = productBoxRegistrations?.find(
                            (p) => p.productBoxNumber === productBox.number,
                        );
                        return {
                            amount: productBoxRegistration?.amount?.toString() ?? '',
                            productBoxName: productBox.name || 'Box ' + productBox.number,
                            productBoxNumber: productBox?.number?.toString() ?? '',
                        };
                    });
                } else {
                    if (productBoxRegistrations && productBoxRegistrations.length > 0) {
                        const selectedProductBox = productBoxes.find(
                            (productBox) => productBox.number === productBoxRegistrations[0].productBoxNumber,
                        );
                        productBoxList = [
                            {
                                amount: productBoxRegistrations[0].amount?.toString() ?? '',
                                productBoxName:
                                    selectedProductBox?.name || 'Box ' + selectedProductBox?.number,
                                productBoxNumber: selectedProductBox?.number?.toString() ?? '',
                            },
                        ];
                    } else {
                        productBoxList = [
                            {
                                amount: '',
                                productBoxName: recentProductBoxNumber
                                    ? 'Box ' + recentProductBoxNumber
                                    : productBoxes[0].name || 'Box ' + productBoxes[0].number,
                                productBoxNumber: recentProductBoxNumber
                                    ? recentProductBoxNumber.toString()
                                    : (productBoxes[0]?.number?.toString() ?? ''),
                            },
                        ];
                    }
                }

                return {
                    activity: activity ?? null,
                    productBoxList: productBoxList,
                    privaSelectOption: privaSelectOption,
                    recentProductBoxNumber,
                    hasDefaultProductBoxNumber,
                };
            },
        ),
    );

    constructor(private store: Store) {
        super(initialState);
    }

    updateActivityAndProductBoxRegistrations = this.updater(
        (
            state,
            {
                activity,
                productBoxRegistrations,
            }: {
                activity: Activity | undefined;
                productBoxRegistrations: ProductBoxRegistration[];
            },
        ): ProductBoxAmountState => {
            const productBoxRegistrationList = productBoxRegistrations.length
                ? productBoxRegistrations
                : undefined;

            if (
                state.activity?.number === activity?.number &&
                areProductBoxRegistrationArraysEqual(
                    productBoxRegistrationList ?? [],
                    state.productBoxRegistrations ?? [],
                )
            ) {
                // circuit breaker, returns old existing state if there's no change in values
                return state;
            }

            // Save multi user recent product box number in local storage
            const firstProductBoxRegistration = productBoxRegistrations[0];
            if (firstProductBoxRegistration && firstProductBoxRegistration.productBoxNumber) {
                this.store.dispatch(
                    updatingMultiUserRecentProductBoxNumber({
                        recentProductBoxNumber: firstProductBoxRegistration.productBoxNumber,
                    }),
                );
            }
            return { ...state, activity, productBoxRegistrations };
        },
    );
}
